import { useCallback } from "react";

import { createNewBall, frameStencilController, getBallMarkers, getPlayerMarkers } from "../../utils";
import { SelectItemApi } from "../use-select-item-api";
import { UpdateFrameStencils } from "./use-update-frame-stencils";

export interface UpdatePossessionProps {
  selectedItems: SelectItemApi["selectedItems"];
}

export type UpdatePossessionFunc = (props: UpdatePossessionProps) => void;

export const useUpdatePossession = (updateFrameStencils: UpdateFrameStencils): UpdatePossessionFunc => {
  return useCallback(
    ({ selectedItems }) => {
      if (selectedItems.length === 0 || selectedItems.length > 1) return;

      const selectedId = selectedItems[0];

      updateFrameStencils((prevState) => {
        if (!prevState) return prevState;
        const ballStencils = getBallMarkers(prevState);
        const frameStencilsWithRemovedBalls = ballStencils.reduce(
          (acc, ball) => frameStencilController({ operation: "REMOVE", stencils: acc, stencil: ball }),
          prevState
        );

        const playerStencils = getPlayerMarkers(frameStencilsWithRemovedBalls);

        const markersWithRemovedPossession = playerStencils
          .filter((stencil) => stencil.id !== selectedId && stencil.possession)
          .map((stencil) => ({ ...stencil, possession: false }));

        const markersWithAddedPossession = playerStencils
          .filter((stencil) => stencil.id === selectedId && !stencil.possession)
          .map((stencil) => ({ ...stencil, possession: true }));

        const frameStencilsWithUpdatedMarkers = [...markersWithRemovedPossession, ...markersWithAddedPossession].reduce(
          (acc, marker) => frameStencilController({ operation: "UPDATE", stencils: acc, stencil: marker }),
          frameStencilsWithRemovedBalls
        );

        const ballStencilsToAdd = markersWithAddedPossession.map((marker) =>
          createNewBall({ pos: marker.pos, associatedPlayer: marker.id })
        );

        return ballStencilsToAdd.reduce(
          (acc, ball) => frameStencilController({ operation: "ADD", stencils: acc, stencil: ball }),
          frameStencilsWithUpdatedMarkers
        );
      });
    },
    [updateFrameStencils]
  );
};
